<template>
  <div>
    <div v-if="placementStartComponentIsSignedOff" class="alert alert-info">
      <strong>Notice:</strong> This plan has been approved. Any changes will require resubmission for approval by your manager.
    </div><!-- /.alert -->

    <div v-if="canApproveEditAccess" class="alert alert-warning d-flex justify-content-between align-items-center">
      <div>
        <strong>Notice:</strong> the graduate has requested to edit the placement
      </div>
      <ladda-button
        type="button"
        class="btn btn-warning ml-3"
        :validate="false"
        :click="confirmBeforeApproveEditAccess"
        :failed="failed"
        :done="requestSubmitted"
      >
        Approve Edit Access
      </ladda-button>
    </div><!-- /.alert -->

    <collapse-card name="directions">
      <template v-slot:title>
        <font-awesome :icon="['far', 'info-circle']" class="text-muted mr-2"/>Directions
      </template>

      <template>
        <p class="strong">Define 3-4 performance objectives for this placement (maximum of 4).</p>

        <p>These enable you to set clear expectations around your performance and priorities throughout your 6-month placement. Include:</p>

        <ul>
          <li>What you will do – objectives, targets, time and measures</li>
          <li>How you will do it – capabilities demonstrated</li>
        </ul>

        <p
          class="strong"
        >When creating your performance objectives you are encouraged to reference the following information:</p>
        <ul>
          <li>Graduate role description</li>
          <li>Team objectives</li>
          <li>Your department’s corporate plan and/or business plans</li>
          <li>Discussions with your manager</li>
          <li>Values and Code of Conduct</li>
          <li>Graduate workbook (Diploma course)</li>
        </ul>

        <p class="strong">When creating your performance objectives follow the SMART principles.</p>

        <img
          src="~assets/img/smart-graphic.jpg"
          alt="Graphic outlinging SMART principles"
          longdesc="#longdesc-smart-graphic"
          class="mx-0"
        >

        <div id="longdesc-smart-graphic" class="sr-only">
          <h2>Specific</h2>
          <p>Define what you will accomplish with no unclear language. What, when, where and how.</p>

          <h2>Measurable</h2>
          <p>Can you track the progress and measure the outcome? Provide metrics or targets.</p>

          <h2>Attainable</h2>
          <p>Is it reasonable to be accomplished and within expectations of role?</p>

          <h2>Relevant</h2>
          <p>Is it consistent with your other objectives, meet your needs and relate to your wider job function?</p>

          <h2>Timely</h2>
          <p>Define a time limit. This will establish a sense of urgency and promote better time management.</p>
        </div><!-- /#longdesc-smart-graphic -->
      </template>
    </collapse-card>

    <collapse-card name="examples">
      <template v-slot:title>Examples</template>

      <p>
        <strong>Performance Objective</strong>
        <br>Research existing policies and guidelines relating to Flexible Working Arrangements in the public and private sector.
      </p>

      <p class="mb-0">
        <strong>Key Milestone</strong>
        <br>Prepare a project scope and project schedule.
      </p>
    </collapse-card>

    <placements-start-component-capability-data-card/>

    <h2 class="mt-5">Performance Objectives</h2>

    <template>
      <placements-start-component-performance-item
        v-for="(objective, index) in objectives"
        :key="index"
        :objective-index="index"
        :objective="objective"
      />
    </template>

    <template v-if="objectives.length < 4">
      <div class="decorative-divider"></div>

      <button type="button" class="btn btn-sm btn-outline-primary" @click="addBlankObjective">
        <font-awesome :icon="['far', 'plus']"/>
        Add Performance Objective
      </button>
    </template>

    <div class="save-actions">
      <div class="alert alert-danger" v-if="otherErrors.length > 0">
        <font-awesome :icon="['fas', 'times']" class="float-right" @click="resetValidation"/>

        <p v-for="(error, index) in otherErrors" :key="`error-${index}`">{{ error }}</p>
      </div>

      <div class="text-right">
        <button
          @click="discard"
          class="btn btn-link btn-discard mr-2"
        >Discard &amp; Exit</button>
        <ladda-button
          type="button"
          class="btn btn-outline-dark mr-2"
          :validate="false"
          :arguments="[{ draft: true, objectives: objectivesWithoutLastMilestone }]"
          :click="storePerformanceObjectives"
          :done="draftSaved"
          :failed="failed"
        >Save &amp; Exit</ladda-button>
        <ladda-button
          type="button"
          class="btn btn-success"
          :validate="false"
          :arguments="[{ draft: false, objectives: objectivesWithoutLastMilestone }]"
          :click="storePerformanceObjectives"
          :failed="failed"
          :done="objectivesCompleted"
        >Next</ladda-button>
      </div>
    </div><!-- /.save-actions -->
  </div>
</template>

<style scoped>
svg.float-right {
  cursor: pointer;
}
</style>

<script>
import { forIn, each, cloneDeep, map } from 'lodash'
import { alert } from 'utils'
import { placementPerformanceObjectiveComputed, placementPerformanceObjectiveMethods, placementMethods, placementComputed } from 'vx/helpers'
import { helpers } from 'mixins/placements'
import CollapseCard from 'components/CollapseCard'
import PlacementsStartComponentCapabilityDataCard from './PlacementsStartComponentCapabilityDataCard'
import PlacementsStartComponentPerformanceItem from './PlacementsStartComponentPerformanceItem'

export function populateObjectiveServerErrors (response) {
  this.otherErrors = []

  forIn(response.data.errors, (errors, name) => {
    let scope
    let field

    if (name.indexOf('.') !== -1) {
      scope = name.split('.').slice(0, -1).join('.')

      field = this.$validator.fields.find({ name: name.split('.').pop(), scope })
    } else {
      scope = null

      field = this.$validator.fields.find({ name })
    }

    this.otherErrors.push(errors[errors.length - 1])

    if (field === undefined) {
      return
    }

    field.setFlags({ invalid: true, valid: false, validated: true })

    each(errors, (msg) => {
      this.errors.add({ field: field.name, id: field.id, msg, scope })
    })
  })
}

export default {
  mixins: [helpers],

  components: {
    CollapseCard,
    PlacementsStartComponentPerformanceItem,
    PlacementsStartComponentCapabilityDataCard
  },

  data () {
    return {
      otherErrors: []
    }
  },

  computed: {
    ...placementComputed,
    ...placementPerformanceObjectiveComputed,

    objectivesWithoutLastMilestone () {
      return map(cloneDeep(this.objectives), (objective) => {
        objective.milestones.pop()

        return objective
      })
    },

    canApproveEditAccess () {
      return (this.$userIsManager || this.$userIsCoordinator) && this.placementStartComponentIsLocked && this.placementStartComponentRequestAccessSent
    }
  },

  async created () {
    await this.getPerformanceObjectives()
  },

  watch: {
    objectives: {
      handler () {
        if (this.objectives.length === 0) {
          this.addBlankObjective()
        }

        if (this.errors.any()) {
          this.$validator.reset()
        }
      },
      deep: true
    }
  },

  methods: {
    ...placementPerformanceObjectiveMethods,
    ...placementMethods,

    resetValidation () {
      this.otherErrors = []

      this.$validator.reset()
    },

    async discard () {
      if (
        !await alert.confirm({
          title: 'Discard Changes?',
          text: 'Are you sure you want to discard your changes.'
        })
      ) {
        return
      }

      this.$router.replace({ name: 'dashboard' })
    },

    failed (error) {
      if (error.response && error.response.status === 422) {
        this.populateObjectiveServerErrors(error.response)
      }
    },

    populateObjectiveServerErrors,

    draftSaved () {
      this.$noty.success('Performance objectives have been saved successfully.')

      this.$router.replace({ name: 'dashboard' })
    },

    async objectivesCompleted () {
      // If we have just completed the objectives but the start component hasn't been updated
      // we'll fire off a request to refetch the placement.
      if (
        this.placementStartComponent.status === null ||
        this.placementStartComponent.performance_objectives_completed === false
      ) {
        await this.getPlacement({ placement: this.placement.id, graduate: this.placement.graduate_id })
      }

      this.$router.push({ name: 'placements.single.start.admin.development' })
    },

    async confirmBeforeApproveEditAccess () {
      if (
        !await alert.confirm({
          title: 'Approve Request Edit Access',
          text: 'Are you sure you want to approve the request to edit access?'
        })
      ) {
        return
      }

      let payload = {
        start_component_id: this.placement.start_component.id
      }

      const response = await this.approveEditAccess(payload)

      if (response && response.data.status === 'success') {
        this.placement.start_component.lock = false
      }
      this.$router.push({ name: 'placements.single.start.admin.performance' })
    },

    requestSubmitted () {
      this.$noty.success('Edit access granted.')
    }
  }
}
</script>
